import { useState, useEffect } from 'react';
import { useColorContext } from '../contexts/ColorContext';
import { PanelHeader } from '../components/shell/PanelHeader';
import { FormatCell } from '../components/ui/FormatCell';
import { useClipboardPresets } from '../hooks/useClipboardPresets';
import { type ColorResult } from '../lib/colorEngine';
import { Plus, Save, X, Edit2, Trash2, Check } from 'lucide-react';
import { useAlert } from '@ascend/alert';


const METRIC_ORDER: { key: keyof ColorResult | 'cmykActive'; label: string; group: string }[] = [
  { key: 'hex', label: 'Hex', group: 'Web' },
  { key: 'rgb', label: 'RGB', group: 'Web' },
  { key: 'hsl', label: 'HSL', group: 'Web' },
  { key: 'hwb', label: 'HWB', group: 'Web' },
  { key: 'oklch', label: 'OKLCH', group: 'Web' },
  { key: 'oklab', label: 'OKLab', group: 'Web' },
  { key: 'displayP3', label: 'Display-P3', group: 'Wide Gamut' },
  { key: 'rec2020', label: 'Rec.2020', group: 'Wide Gamut' },
  { key: 'lab', label: 'CIELAB', group: 'Perceptual' },
  { key: 'lch', label: 'CIELCH', group: 'Perceptual' },
  { key: 'luv', label: 'CIE Luv', group: 'Perceptual' },
  { key: 'lchuv', label: 'LChuv', group: 'Perceptual' },
  { key: 'hunterLab', label: 'Hunter Lab', group: 'Perceptual' },
  { key: 'cmykActive', label: 'CMYK (Selected Profile)', group: 'Print' },
  { key: 'hsv', label: 'HSV', group: 'Legacy' },
  { key: 'xyz', label: 'XYZ (D65)', group: 'Legacy' },
  { key: 'xyY', label: 'xyY', group: 'Legacy' },
  { key: 'ycbcr', label: 'YCbCr', group: 'Legacy' },
  { key: 'binary', label: 'Binary', group: 'Legacy' },
];

export function FormatsTool() {
  const {
    displayResult, result, copiedField, copyToClipboard,
    formatValue, getCssFunctional,
  } = useColorContext();

  const renderResult = displayResult;

  if (!renderResult) {
    return (
      <>
        <PanelHeader title="All formats" subtitle="Enter a valid color to see results." />
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Enter a valid color to see results.
        </div>
      </>
    );
  }

  return (
    <>
      <PanelHeader
        title="All formats"
        subtitle={`Every color space for ${renderResult.hex.toUpperCase()} — click any value to copy`}
        actions={
          <button
            style={{
              padding: '6px 12px',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius)',
              fontSize: 12,
              background: 'var(--panel)',
              color: 'var(--ink)',
              cursor: 'pointer',
            }}
            onClick={() => {
              const data: Record<string, unknown> = {};
              METRIC_ORDER.forEach(({ key, label }) => {
                const value = renderResult[key as keyof ColorResult];
                if (value !== undefined && value !== null) {
                  data[label] = value;
                }
              });
              copyToClipboard(JSON.stringify(data, null, 2), 'all-json');
            }}
          >
            <span role="status">{copiedField === 'all-json' ? '✓ Copied' : 'Copy all as JSON'}</span>
          </button>
        }
      />
      {Object.entries(
        METRIC_ORDER.reduce<Record<string, typeof METRIC_ORDER>>((acc, item) => {
          (acc[item.group] ??= []).push(item);
          return acc;
        }, {})
      ).map(([groupName, items]) => {
        const visible = items.filter(({ key }) => {
          const v = renderResult[key as keyof ColorResult];
          return v !== undefined && v !== null;
        });
        if (visible.length === 0) return null;
        return (
          <section key={groupName} aria-labelledby={`group-${groupName}`}>
            <h3 id={`group-${groupName}`} className="format-group__label">{groupName}</h3>
            <div className="grid-3">
              {visible.map(({ key, label }) => {
                const value = renderResult[key as keyof ColorResult];
                const fieldKey = typeof key === 'string' ? key : label;
                const formattedValue = formatValue(value, 0);
                const cssValue = getCssFunctional(key, value);
                return (
                  <FormatCell
                    key={`${groupName}-${fieldKey}`}
                    label={label}
                    value={formattedValue}
                    cssValue={cssValue || null}
                    copied={copiedField === fieldKey}
                    onCopy={() => copyToClipboard(cssValue || formattedValue, fieldKey)}
                  />
                );
              })}
            </div>
          </section>
        );
      })}

      {/* ── Custom Clipboard Presets (C5) ────────────────────────── */}
      <CustomPresetsSection hex={renderResult.hex} copiedField={copiedField} onCopy={copyToClipboard} />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Custom Presets Section — renders user-defined clipboard format presets
   ═══════════════════════════════════════════════════════════════════════════ */

function CustomPresetsSection({ hex, copiedField, onCopy }: {
  hex: string;
  copiedField: string | null;
  onCopy: (text: string, label: string) => void;
}) {
  const { presets, addPreset, updatePreset, deletePreset, applyPreset, loading } = useClipboardPresets();

  // Filter to only show non-system presets (user-created)
  // System presets (HEX, RGB, HSL etc.) are already shown in the built-in groups above
  const SYSTEM_FORMATS = new Set(['hex', 'rgb', 'rgba', 'hsl']);
  const customPresets = presets.filter(p => !SYSTEM_FORMATS.has(p.format));

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [template, setTemplate] = useState('');
  const [editName, setEditName] = useState('');
  const [editTemplate, setEditTemplate] = useState('');

  // Reset add form
  const resetAdd = () => {
    setIsAdding(false);
    setName('');
    setTemplate('');
  };

  const handleAdd = async () => {
    if (!name.trim() || !template.trim()) return;
    await addPreset(name.trim(), name.trim().toLowerCase().replace(/\s+/g, '-'), template.trim());
    resetAdd();
  };

  const startEdit = (id: string, n: string, t: string) => {
    setEditingId(id);
    setEditName(n);
    setEditTemplate(t);
  };

  const handleSave = async () => {
    if (!editingId || !editName.trim() || !editTemplate.trim()) return;
    await updatePreset(editingId, { name: editName.trim(), template: editTemplate.trim() });
    setEditingId(null);
  };

  const { confirm } = useAlert();

  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Delete preset?', message: 'This action cannot be undone.', danger: true });
    if (ok) {
      await deletePreset(id);
    }
  };

  // Track locally copied state for presets
  const [localCopied, setLocalCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!localCopied) return;
    const t = setTimeout(() => setLocalCopied(null), 1500);
    return () => clearTimeout(t);
  }, [localCopied]);

  if (loading) return null;

  return (
    <section aria-labelledby="group-custom">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 id="group-custom" className="format-group__label">Custom</h3>
        {!isAdding && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', fontSize: 'var(--t-caption)',
              fontWeight: 600, fontFamily: 'inherit',
              background: 'var(--accent)', color: 'white',
              border: 'none', borderRadius: 'var(--radius-lg)',
              cursor: 'pointer', transition: 'filter 100ms',
            }}
          >
            <Plus size={12} /> Add
          </button>
        )}
      </div>

      {/* Add form */}
      {isAdding && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 'var(--s-2)',
          padding: 'var(--s-3)', marginBottom: 'var(--s-3)',
          background: 'var(--panel-2)', borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--line)',
        }}>
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Name (e.g., Swift UIColor)"
            style={{
              padding: '6px 10px', fontSize: 'var(--t-caption)',
              border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
              background: 'var(--panel)', color: 'var(--ink)', fontFamily: 'inherit',
            }}
          />
          <input
            type="text" value={template} onChange={e => setTemplate(e.target.value)}
            placeholder="Template: UIColor(red: {r}/255, green: {g}/255, blue: {b}/255, alpha: {a})"
            style={{
              padding: '6px 10px', fontSize: 'var(--t-caption)',
              border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
              background: 'var(--panel)', color: 'var(--ink)',
              fontFamily: 'var(--font-mono)',
            }}
          />
          <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
            Variables: {'{hex}'} {'{r}'} {'{g}'} {'{b}'} {'{a}'} {'{h}'} {'{s}'} {'{l}'}
          </div>
          <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
            <button type="button" onClick={handleAdd} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 12px', fontSize: 'var(--t-caption)', fontWeight: 600,
              background: 'var(--accent)', color: 'white',
              border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              fontFamily: 'inherit',
            }}><Save size={12} /> Save</button>
            <button type="button" onClick={resetAdd} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 12px', fontSize: 'var(--t-caption)', fontWeight: 600,
              background: 'transparent', color: 'var(--ink-2)',
              border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              fontFamily: 'inherit',
            }}><X size={12} /> Cancel</button>
          </div>
        </div>
      )}

      {/* Preset cards */}
      {customPresets.length === 0 && !isAdding ? (
        <div style={{
          padding: 'var(--s-4)', textAlign: 'center',
          color: 'var(--ink-3)', fontSize: 'var(--t-caption)',
          border: '1px dashed var(--line-2)', borderRadius: 'var(--radius-lg)',
          background: 'var(--panel-2)',
        }}>
          No custom presets yet. Click "Add" to create one.
        </div>
      ) : (
        <div className="grid-3">
          {customPresets.map(preset => {
            const previewValue = applyPreset(preset.id!, hex);
            const fieldKey = `custom-${preset.id}`;
            const isCopied = copiedField === fieldKey || localCopied === fieldKey;

            if (editingId === preset.id) {
              return (
                <div key={preset.id} style={{
                  padding: 'var(--s-3)', background: 'var(--panel-2)',
                  border: '1px solid var(--accent)', borderRadius: 'var(--radius-lg)',
                  display: 'flex', flexDirection: 'column', gap: 'var(--s-2)',
                }}>
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                    style={{
                      padding: '4px 8px', fontSize: 'var(--t-caption)',
                      border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
                      background: 'var(--panel)', color: 'var(--ink)', fontFamily: 'inherit',
                    }}
                  />
                  <input type="text" value={editTemplate} onChange={e => setEditTemplate(e.target.value)}
                    style={{
                      padding: '4px 8px', fontSize: 'var(--t-caption)',
                      border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
                      background: 'var(--panel)', color: 'var(--ink)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 'var(--s-1)' }}>
                    <button type="button" onClick={handleSave} style={{
                      padding: '3px 8px', fontSize: 10, fontWeight: 600,
                      background: 'var(--accent)', color: 'white',
                      border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}><Check size={10} /> Save</button>
                    <button type="button" onClick={() => setEditingId(null)} style={{
                      padding: '3px 8px', fontSize: 10,
                      background: 'transparent', color: 'var(--ink-2)',
                      border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}>Cancel</button>
                  </div>
                </div>
              );
            }

            return (
              <FormatCell
                key={fieldKey}
                label={preset.name}
                value={previewValue}
                cssValue={previewValue}
                copied={isCopied}
                onCopy={() => {
                  onCopy(previewValue, fieldKey);
                  setLocalCopied(fieldKey);
                }}
                actions={
                  <span style={{ display: 'inline-flex', gap: 2, marginLeft: 4 }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); startEdit(preset.id!, preset.name, preset.template); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--ink-3)' }}
                      title="Edit" aria-label={`Edit ${preset.name}`}
                    ><Edit2 size={11} /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(preset.id!); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--semantic-fail, #dc2626)' }}
                      title="Delete" aria-label={`Delete ${preset.name}`}
                    ><Trash2 size={11} /></button>
                  </span>
                }
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
